
/**
 * LegalPage — the shared shell for /privacy and /terms.
 *
 * Legal copy gets a compact text-only header rather than the full-bleed
 * `PageHeader`: a 52vh photo above a privacy policy is decoration nobody came
 * for, and these pages have no photo of their own to give. It keeps the brand
 * language otherwise (flame eyebrow, condensed uppercase Archivo title,
 * square corners) and clears the fixed nav through var(--nav-h), never a
 * hardcoded pt-* value.
 *
 * Body copy is styled with explicit child selectors because this project has
 * no @tailwindcss/typography plugin.
 */
const PROSE = [
  "text-base leading-relaxed text-graphite/75",
  "[&_h2]:font-heading [&_h2]:mb-4 [&_h2]:mt-12 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-graphite",
  "[&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-graphite",
  "[&_p]:mb-4",
  "[&_ul]:mb-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6",
  "[&_strong]:font-semibold [&_strong]:text-graphite",
  "[&_a]:font-medium [&_a]:text-flame [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-flame-dark",
].join(" ");

export interface LegalPageProps {
  title: string;
  /** Human readable date, e.g. "July 26, 2026". Shown under the title. */
  updated: string;
  children: React.ReactNode;
}

export function LegalPage({ title, updated, children }: LegalPageProps) {
  return (
    <main>
      <section className="bg-graphite pt-[var(--nav-h)]">
        <div className="mx-auto max-w-4xl px-4 pb-14 pt-16 sm:px-6 lg:px-8 lg:pb-20 lg:pt-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame">
            Legal
          </p>
          <h1 className="font-heading mt-4 font-stretch-[62.5%] text-4xl font-black uppercase leading-[0.9] text-white sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-5 text-sm text-white/60">Last updated: {updated}</p>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className={PROSE}>{children}</div>
        </div>
      </section>
    </main>
  );
}
